// Advanced Audio Processing Utility

class AudioProcessor {
  constructor() {
    this.audioContext = null;
    this.sourceNode = null;
    this.destinationNode = null;
    this.processingNodes = [];
    this.isProcessingEnabled = false;
    this.originalStream = null;
    this.processedStream = null;
  }

  async initialize(stream) {
    if (!stream) return null;
    
    this.originalStream = stream;
    
    // Create audio context
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: 48000,
    });
    
    // Create source node from the original stream
    this.sourceNode = this.audioContext.createMediaStreamSource(stream);
    
    // Create destination node
    this.destinationNode = this.audioContext.createMediaStreamDestination();
    
    // Connect source directly to destination initially
    this.sourceNode.connect(this.destinationNode);
    
    // Store the processed stream
    this.processedStream = this.destinationNode.stream;
    
    return this.processedStream;
  }

  enableNoiseReduction(enabled) {
    if (!this.audioContext || !this.sourceNode || !this.destinationNode) {
      console.error('Audio processor not initialized');
      return;
    }
    
    // Disconnect existing processing chain
    this.sourceNode.disconnect();
    this.processingNodes.forEach(node => node.disconnect());
    this.processingNodes = [];
    
    if (enabled) {
      // Create a high-pass filter to remove low frequency noise
      const highpassFilter = this.audioContext.createBiquadFilter();
      highpassFilter.type = 'highpass';
      highpassFilter.frequency.value = 80; // Cut off frequencies below 80 Hz
      highpassFilter.Q.value = 0.7;
      this.processingNodes.push(highpassFilter);
      
      // Create a low-pass filter to remove high frequency noise
      const lowpassFilter = this.audioContext.createBiquadFilter();
      lowpassFilter.type = 'lowpass';
      lowpassFilter.frequency.value = 6000; // Cut off frequencies above 6000 Hz
      lowpassFilter.Q.value = 0.7;
      this.processingNodes.push(lowpassFilter);
      
      // Create a compressor to even out audio levels
      const compressor = this.audioContext.createDynamicsCompressor();
      compressor.threshold.value = -50;
      compressor.knee.value = 40;
      compressor.ratio.value = 12;
      compressor.attack.value = 0;
      compressor.release.value = 0.25;
      this.processingNodes.push(compressor);
      
      // Create a gain node to control output volume
      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = 1.0; // Normal volume
      this.processingNodes.push(gainNode);
      
      // Connect all nodes in the processing chain
      this.sourceNode.connect(highpassFilter);
      highpassFilter.connect(lowpassFilter);
      lowpassFilter.connect(compressor);
      compressor.connect(gainNode);
      gainNode.connect(this.destinationNode);
      
      this.isProcessingEnabled = true;
    } else {
      // Bypass processing - connect source directly to destination
      this.sourceNode.connect(this.destinationNode);
      this.isProcessingEnabled = false;
    }
    
    return this.processedStream;
  }

  getProcessedStream() {
    return this.processedStream;
  }

  getOriginalStream() {
    return this.originalStream;
  }

  isEnabled() {
    return this.isProcessingEnabled;
  }

  dispose() {
    if (this.audioContext) {
      this.sourceNode.disconnect();
      this.processingNodes.forEach(node => node.disconnect());
      this.audioContext.close();
      this.audioContext = null;
      this.sourceNode = null;
      this.destinationNode = null;
      this.processingNodes = [];
      this.processedStream = null;
    }
  }
}

export default AudioProcessor;